import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TertiaryButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import { PlusOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/PlusOutlined';
import { MinusOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/MinusOutlined';

// eslint-disable-next-line import/no-unresolved
import getConfig from 'next/config';
import FacetAccordion from './facet-accordion/FacetAccordion';
import OutOfStockFilter from './out-of-stock-filter/OutOfStockFilter';
import AppliedFilters from './applied-filters/AppliedFilters';
import FacetsHeader from './facets-header/FacetsHeader';
import GridColumn from '../grid/GridColumn';
import styles from './Facets.module.scss';

import { getNumberOfFilters } from './applied-filters/helpers';
import {
  FILTER_INTERACTION,
  dispatchImmediateAnalyticsEvent,
} from '../analytics/analyticsEvents';

import SingleItemFacet from './single-item-facet/SingleItemFacet';
import { useFacetsData } from './data/useFacetsData';

const { publicRuntimeConfig: features } = getConfig();

const INITIAL_NUMBER_TO_SHOW = 5;
const MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING = 7;

export const Facets = ({
  isFilterDrawer = false,
  isMobile = false,
  isPrimaryFacetMainFacet = false,
  openFacetName = '',
}) => {
  const [isFacetListExpanded, setIsFacetListExpanded] = useState(false);

  const [isPlpBodySentinelOffTopOfPage, setIsPlpBodySentinelOffTopOfPage] =
    useState(null);

  const {
    experiences,
    facetContext,
    isHeaderPeeping,
    plpBodySentinelRef,
    isCompareBarOpen,
    searchContext,
  } = useFacetsData();

  const facetsElRef = useRef(null);

  const checkTopOfPlpBodyInView = () => {
    const el = plpBodySentinelRef;
    if (!el) {
      return false;
    }
    const rect = el.getBoundingClientRect();
    setIsPlpBodySentinelOffTopOfPage(rect.top < 0);
  };

  useEffect(() => {
    checkTopOfPlpBodyInView();
  }, [plpBodySentinelRef, isPlpBodySentinelOffTopOfPage]);

  useEffect(() => {
    document.addEventListener('scroll', checkTopOfPlpBodyInView);
    window.addEventListener('resize', checkTopOfPlpBodyInView);
    return () => {
      document.removeEventListener('scroll', checkTopOfPlpBodyInView);
      window.removeEventListener('resize', checkTopOfPlpBodyInView);
    };
  }, [plpBodySentinelRef, isPlpBodySentinelOffTopOfPage]);

  const {
    isAllFacetsClosedFlagOn,
    isFacetLimitOn,
    isLeftNavHeaderFlagOn,
    isTabletEnhancementsFlagOn,
    isSingleColumnLayout,
  } = experiences;

  const {
    numberOfFiltersToShowClear,
    numberOfFiltersToShowClearSticky,
    numberOfOpenFacetAccordions,
  } = features;

  const {
    baseFacetId,
    facets,
    outOfStockInfo,
    primaryFacet,
    showHideOutOfStockFilter,
    singleItemFacet,
  } = facetContext;

  const { filter } = searchContext;
  const numberOfFilters = getNumberOfFilters(filter);
  const showClearFiltersSticky =
    numberOfFiltersToShowClearSticky <= numberOfFilters;

  const facetsToShow = facets.filter((facet) => facet.show);

  const isShowMoreOrFewerVisible =
    facetsToShow.length > MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING;

  const maxFacetsToDisplay =
    !isMobile &&
    isFacetLimitOn &&
    isShowMoreOrFewerVisible &&
    !isFacetListExpanded
      ? INITIAL_NUMBER_TO_SHOW
      : facetsToShow.length;

  const isAccordionOpen = (index, facetName) => {
    if (!isPrimaryFacetMainFacet) {
      return isAllFacetsClosedFlagOn
        ? false
        : index < numberOfOpenFacetAccordions;
    }
    return facetName === primaryFacet?.name;
  };

  const toggleShowMore = useCallback(
    (toggleValue) => {
      dispatchImmediateAnalyticsEvent(FILTER_INTERACTION, {
        type: toggleValue ? 'show more' : 'show fewer',
      });
      setIsFacetListExpanded(toggleValue);
    },
    [isFacetListExpanded]
  );

  const onClearAllClick = () => {
    if (facetsElRef?.current.scroll) {
      facetsElRef.current.scroll(0, 0);
    }
  };

  const isSelectedFacet = (facetName) =>
    facetName === 'On Offer' || facetName === openFacetName;
  return (
    <GridColumn
      className={classNames(styles.plp__facets, {
        [styles['plp__facets--desktop']]: !isMobile,
        [styles['tabletEnhancements']]: isTabletEnhancementsFlagOn,
        [styles['plp__facets--compare-open']]: isCompareBarOpen,
        [styles['plp__facets--lhn-header-on']]:
          !isMobile && isLeftNavHeaderFlagOn,
        [styles.isHeaderPeeping]: isHeaderPeeping,
        [styles.isPlpBodySentinelOffTop]: isPlpBodySentinelOffTopOfPage,
        [styles['plp__facets--single-column']]:
          isFilterDrawer && isSingleColumnLayout,
      })}
      colLg={isMobile ? undefined : 3}
      colXl={3}
      data-testid="facet-container"
    >
      <nav
        ref={facetsElRef}
        aria-label="Filters"
        data-testid="component-facets"
        className={classNames(styles.facets, {
          [styles['facets--compare-open']]: isCompareBarOpen,
          [styles['facets--header-peeping']]: isHeaderPeeping,
          [styles['facets--mobile']]: isMobile,
          [styles['facets--lhn-header-on']]: isLeftNavHeaderFlagOn,
        })}
      >
        {!isMobile && isLeftNavHeaderFlagOn && (
          <FacetsHeader // contains a visible h2
            baseFacetId={baseFacetId}
            showClearFiltersSticky={showClearFiltersSticky}
            onClearAllClick={onClearAllClick}
          />
        )}
        {!isMobile && !isLeftNavHeaderFlagOn && (
          <h2 className="visuallyHidden">Filters</h2>
        )}

        <AppliedFilters
          baseFacetId={baseFacetId}
          numberOfFiltersToShowClear={numberOfFiltersToShowClear}
          showClearFiltersLink={!isMobile && !isLeftNavHeaderFlagOn}
          showHeader={!isLeftNavHeaderFlagOn}
          mobile={isMobile}
          isFilterDrawer={isFilterDrawer}
          showHideOutOfStockFilter={showHideOutOfStockFilter}
        />
        <OutOfStockFilter
          showHideOutOfStockFilter={showHideOutOfStockFilter}
          outOfStockInfo={outOfStockInfo}
        />
        <SingleItemFacet facet={singleItemFacet} />
        {facetsToShow.map((facet, index) => (
          <FacetAccordion
            href="/"
            key={facet.dimensionName}
            opened={
              isSingleColumnLayout
                ? isSelectedFacet(facet.name)
                : isAccordionOpen(index, facet.name)
            }
            facet={facet}
            showColourFilterSwatches
            isFilterDrawer={isFilterDrawer}
            position={index + 1}
            isShown={
              isMobile ||
              !isFacetLimitOn ||
              isFacetListExpanded ||
              index < maxFacetsToDisplay
            }
          />
        ))}
        {!isMobile && isFacetLimitOn && isShowMoreOrFewerVisible && (
          <TertiaryButton
            data-testid="toggle-more-facets"
            onClick={() => toggleShowMore(!isFacetListExpanded)}
            className={styles['toggle-more-facets']}
            small={true}
          >
            <span className={styles.showMoreButton}>
              {isFacetListExpanded ? (
                <MinusOutlined
                  className={styles.marginRight}
                  title="Collapse section"
                  size="medium"
                />
              ) : (
                <PlusOutlined
                  className={styles.marginRight}
                  title="Expand section"
                  size="medium"
                />
              )}
              Show {isFacetListExpanded ? 'fewer' : 'more'} filters
            </span>
          </TertiaryButton>
        )}
      </nav>
    </GridColumn>
  );
};

Facets.propTypes = {
  isFilterDrawer: PropTypes.bool,
  isMobile: PropTypes.bool,
  isPrimaryFacetMainFacet: PropTypes.bool,
};

export default Facets;
